import Link from 'next/link';
import { useRouter } from 'next/router';
import type { SyntheticEvent } from 'react';
import styled from 'styled-components';

import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';

import { isInMobile } from '@fabric-app/compat';
import { VaulDrawer } from '@fabric-app/ui/components/VaulDrawer';

import { TypographyContainer } from '@fabric-app/ui/components/Typography';
import { REDIRECTOR_URL } from '@/src/services/woody/woody';
import { inAppBrowserDebug, isNextDev } from '@/src/constants/env';
import { ListItem, ListContainer, ListItemTitle } from '@fabric-app/ui/components/List/List';
import { Flex } from '@fabric-app/ui/components/Flex';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { useOpenInNativeDrawerContext } from '../providers/OpenInNativeDrawerProvider';
import { openNativeMobileApp } from '../utils/openNativeMobileApp';
import { ANDROID_MOBILE_DEEPLINK, IOS_MOBILE_DEEPLINK } from '@/src/hooks/mobile';
import { trimStartChars } from '@fabric-app/utils/text';
import { GlobeIcon } from '@fabric-app/icons/icons';
import { ImgFabricApp } from '@fabric-app/icons/old';

const FabricAppIcon = styled.img.attrs({
  src: ImgFabricApp,
  alt: 'Fabric App',
})`
  color: ${cssVar['color-app-primary']};
  width: 24px;
  height: auto;
  object-fit: contain;
  image-rendering: pixelated;
`;

/**
 * @TODO - Possibly make this into a variant of ListItem.
 * This type of list will be used in multiple places when we start switching our existing modals and dialogs on mobile to drawers.
 */
const Item = styled(ListItem)`
  background: ${cssVar['color-bg-secondary']};
  padding: 1rem;
  border: none;

  border-radius: 0px;

  & + & {
    margin-top: 0px;
  }

  &:first-child {
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
  }

  &:last-child {
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${cssVar['color-border-secondary']};
  }

  ${mediaHover} {
    &:hover {
      border-color: ${cssVar['color-border-secondary']};
      opacity: 0.8;
    }
  }
`;

const OpenInNativeAppDrawer: React.FC = () => {
  const { mobileOS, urlPath, isPending, isVisible, closeDrawer } = useOpenInNativeDrawerContext();
  const router = useRouter();

  const options = [
    {
      icon: <FabricAppIcon />,
      title: `Fabric ${mobileOS} app`,
      props: {
        as: Link,
        href: `${REDIRECTOR_URL}${urlPath}`,
        onClick: (e: SyntheticEvent) => {
          e.preventDefault();
          openNativeMobileApp(
            `${mobileOS === 'iOS' ? IOS_MOBILE_DEEPLINK : ANDROID_MOBILE_DEEPLINK}://${trimStartChars(
              urlPath,
              ['/'],
            )}`,
            `${REDIRECTOR_URL}${urlPath}`,
          );
        },
      },
    },
    {
      icon: <GlobeIcon size={24} />,
      title: 'Web browser',
      props: {
        onClick: closeDrawer,
      },
    },
  ];

  if (
    isInMobile() ||
    (isNextDev && !inAppBrowserDebug) ||
    isPending ||
    router.pathname === appRoutes.auth.confirmEmail
  ) {
    return null;
  }

  return (
    <VaulDrawer.Root open={isVisible} onOpenChange={(open) => (!open ? closeDrawer() : undefined)}>
      <VaulDrawer.Portal>
        <VaulDrawer.Overlay />
        <VaulDrawer.Content>
          <VaulDrawer.Handle />
          <VaulDrawer.Header>
            <VaulDrawer.Title>Open this in...</VaulDrawer.Title>
          </VaulDrawer.Header>
          <Flex direction="column" gap="sectionsMedium" style={{ padding: '0 1rem 1rem 1rem' }}>
            <ListContainer>
              {options.map((option, index) => (
                <Item key={index} {...option.props}>
                  <Flex
                    gap="elementsContainer"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={{ overflow: 'hidden', flexShrink: 1 }}
                  >
                    {option.icon}
                    <TypographyContainer>
                      <ListItemTitle>{option.title}</ListItemTitle>
                    </TypographyContainer>
                  </Flex>
                </Item>
              ))}
            </ListContainer>
          </Flex>
        </VaulDrawer.Content>
      </VaulDrawer.Portal>
    </VaulDrawer.Root>
  );
};

export default OpenInNativeAppDrawer;
